/**
 * TypeScript interfaces for offline system (quotes, meetings, etc.)
 * Following OBY component-patterns.json: PascalCase interfaces with optional properties marked with ?
 */

import type { EventType, MeetingStatus, MeetingType } from "@/types/meeting.types";

export type SyncStatus = "pending" | "syncing" | "synced" | "failed";

/**
 * Offline quote stored in IndexedDB
 * Contains full quote data that will be synced to Supabase
 */
export interface OfflineQuote {
  id: string; // UUID generated locally
  project_id: number;
  customer_name: string;
  customer_email?: string;
  checklist_template_id?: number;
  quote_data: Record<string, unknown>; // Full quote JSON
  total_amount: number;
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  sync_status: SyncStatus;
  retry_count?: number;
  last_error?: string;
  has_pending_images?: boolean; // True if this quote has images pending upload
  pending_image_count?: number; // Number of images waiting to upload
}

/**
 * Offline image stored in IndexedDB
 * Contains image blob and metadata for upload when online
 */
export interface OfflineImage {
  id: string; // UUID generated locally
  quote_id: string; // Links to OfflineQuote.id
  field_name: string; // Which field this image belongs to (e.g., "bathroom_photos")
  component_id?: number; // Optional component ID for component-specific images
  blob: Blob; // Actual image data (compressed)
  file_name: string; // Original filename
  file_size: number; // Size in bytes
  mime_type: string; // e.g., "image/jpeg"
  created_at: string; // ISO timestamp
  sync_status: SyncStatus;
  retry_count?: number;
  last_error?: string;
  uploaded_url?: string; // R2 URL after successful upload
}

/**
 * Offline meeting stored in IndexedDB
 * Contains meeting data that will be synced to API
 */
export interface OfflineMeeting {
  id: string; // UUID generated locally (e.g., 'offline_1234567890') or number after sync
  customer_id: number;
  project_id: number | null;
  meeting_date: string; // ISO format: YYYY-MM-DD
  meeting_time: string; // 24h format: HH:MM
  duration_minutes: number;
  event_type: EventType; // Type of calendar event (meeting, task, reminder)
  meeting_type: MeetingType;
  status: MeetingStatus;
  location: string | null;
  notes: string | null;
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  sync_status: SyncStatus;
  retry_count?: number;
  last_error?: string;
  offline_created: boolean; // True if created offline
}

/**
 * Sync queue item for tracking pending uploads
 */
export interface SyncQueueItem {
  id: string; // Matches OfflineQuote.id or OfflineMeeting.id
  type: "quote" | "project" | "meeting" | "custom"; // Extensible for future offline features
  data: Record<string, unknown>;
  created_at: string;
  retry_count: number;
  last_attempted_at?: string;
  error?: string;
}

/**
 * Cached customer data for offline access
 */
export interface CachedCustomer {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  created_at: string;
  cached_at: string; // When this data was cached
}

/**
 * Cached product data for offline access
 */
export interface CachedProduct {
  id: number;
  name: string;
  category: string;
  price: number;
  description?: string;
  image_url?: string;
  cached_at: string;
}

/**
 * Cached service data for offline access
 */
export interface CachedService {
  id: number;
  name: string;
  description?: string;
  base_price: number;
  pricing_type?: string;
  cached_at: string;
}

/**
 * Cached meeting data for offline access
 * Includes customer info for display without additional lookups
 */
export interface CachedMeeting {
  id: number;
  customer_id: number;
  customer_name: string;
  customer_email?: string;
  project_id: number | null;
  meeting_date: string;
  meeting_time: string;
  duration_minutes: number;
  event_type: EventType; // Type of calendar event (meeting, task, reminder)
  meeting_type: MeetingType;
  status: MeetingStatus;
  location: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  cached_at: string;
}

/**
 * Sync metadata for tracking last sync times
 */
export interface SyncMetadata {
  key: "customers" | "products" | "services" | "projects" | "meetings";
  last_synced_at: string; // ISO timestamp
  record_count: number;
  status: "success" | "partial" | "failed";
  error?: string;
}

/**
 * Offline data store interface for type safety
 */
export interface OfflineDataStore {
  "pending-quotes": OfflineQuote;
  "pending-images": OfflineImage;
  "offline-meetings": OfflineMeeting;
  "cached-customers": CachedCustomer;
  "cached-products": CachedProduct;
  "cached-services": CachedService;
  "cached-meetings": CachedMeeting;
  "sync-queue": SyncQueueItem;
  "sync-metadata": SyncMetadata;
}

/**
 * Database schema version for migrations
 */
export const DB_VERSION = 3; // v3: Added pending-images store for offline image handling
export const DB_NAME = "reno-quote-offline";
